/**
 * Security and sanitization utilities for QR Studio.
 * Ensures untrusted user input, uploads, and QR payloads are safely inspected.
 */

const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'vbscript:',
  'data:',
  'file:',
  'blob:',
  'about:'
];

/**
 * Validates whether a URL protocol is safe.
 */
export function isSafeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  
  // Check for dangerous protocol prefix
  for (const protocol of DANGEROUS_PROTOCOLS) {
    if (trimmed.startsWith(protocol)) {
      return false;
    }
  }

  // Check for control characters or hidden scripts
  if (/[\x00-\x1F\x7F]/.test(url)) {
    return false;
  }

  return true;
}

/**
 * Sanitizes and normalizes an input URL.
 * Rejects malicious schemes and defaults un-prefixed domains to https://
 */
export function sanitizeUrl(input: string): string {
  if (!input) return '';
  const trimmed = input.trim();

  if (!isSafeUrl(trimmed)) {
    return '';
  }

  // If starts with scheme, verify it's http/https/mailto/tel/sms/geo/upi
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) {
    return trimmed;
  }

  // Default to https if no protocol specified
  return `https://${trimmed}`;
}

/**
 * Safely sanitizes SVG text to prevent XSS / script execution when embedding user SVGs as logos.
 */
export function sanitizeSvgString(svgText: string): string {
  if (typeof window === 'undefined') return svgText;
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    
    // Check for parse errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid SVG format');
    }

    const svgElement = doc.documentElement;
    if (svgElement.nodeName.toLowerCase() !== 'svg') {
      throw new Error('Not an SVG document');
    }

    // Remove dangerous tags
    const dangerousTags = ['script', 'iframe', 'object', 'embed', 'foreignObject', 'link', 'style', 'base'];
    dangerousTags.forEach(tag => {
      const elements = svgElement.querySelectorAll(tag);
      elements.forEach(el => el.remove());
    });

    // Remove inline event handlers and javascript: attributes from all elements
    const allElements = svgElement.querySelectorAll('*');
    allElements.forEach(el => {
      // Loop over attributes backwards to safely remove
      for (let i = el.attributes.length - 1; i >= 0; i--) {
        const attr = el.attributes[i];
        const attrName = attr.name.toLowerCase();
        const attrVal = attr.value.trim().toLowerCase();

        if (attrName.startsWith('on') || attrVal.startsWith('javascript:') || attrVal.startsWith('data:')) {
          el.removeAttribute(attr.name);
        }
      }
    });

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch (err) {
    console.error('Failed to sanitize SVG:', err);
    return '';
  }
}

/**
 * Escapes arbitrary strings for safe HTML rendering if needed.
 */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Analyzes a decoded URL for security risks.
 */
export function analyzeDecodedUrl(raw: string): {
  isUrl: boolean;
  protocol?: string;
  hostname?: string;
  pathname?: string;
  searchParams?: Record<string, string>;
  warnings: string[];
  isSafe: boolean;
} {
  const warnings: string[] = [];
  let isSafe = true;

  if (!raw || typeof raw !== 'string') {
    return { isUrl: false, warnings: [], isSafe: true };
  }

  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();

  for (const dangerous of DANGEROUS_PROTOCOLS) {
    if (lower.startsWith(dangerous)) {
      warnings.push(`Contains potentially dangerous protocol (${dangerous})`);
      return { isUrl: false, warnings, isSafe: false };
    }
  }

  try {
    const urlObj = new URL(trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`);
    const isUrl = Boolean(urlObj.hostname && urlObj.hostname.includes('.'));

    if (isUrl) {
      if (urlObj.protocol === 'http:') {
        warnings.push('Unencrypted connection (HTTP). Information sent may not be secure.');
      }

      // Check for IP address as host
      if (/^(\d{1,3}\.){3}\d{1,3}$/.test(urlObj.hostname)) {
        warnings.push('URL points directly to an IP address instead of a domain name.');
      }

      // Check for suspicious executable file extensions
      const pathLower = urlObj.pathname.toLowerCase();
      if (/\.(exe|apk|dmg|pkg|bat|cmd|vbs|scr|msi)$/.test(pathLower)) {
        warnings.push('URL appears to link directly to an executable/installer file.');
        isSafe = false;
      }

      // Extract search params safely
      const searchParams: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        searchParams[key] = value;
      });

      return {
        isUrl: true,
        protocol: urlObj.protocol,
        hostname: urlObj.hostname,
        pathname: urlObj.pathname,
        searchParams,
        warnings,
        isSafe
      };
    }
  } catch {
    // Not a URL
  }

  return {
    isUrl: false,
    warnings,
    isSafe
  };
}
